<!--
  상태:
    draft: 작성 중 (수정 가능)
    submitted: 제출됨 (승인 대기 중, 수정 불가)
    approved: 승인됨 (조회만 가능)
    rejected: 반려됨 (수정 가능)

  사용자 권한:
    작성자: 작성 및 수정 가능 (draft, rejected 상태)
    승인자: 승인 가능 (submitted 상태)
    일반 사용자: 조회만 가능 (approved 상태)
  
  화면의 모드
    (viewMode, editMode, approveMode)를 상태와 권한에 따라 동적으로 설정.
-->
<template>
  <div class="expenditure-form">
    <el-card shadow="hover">

      <!-- 헤더: 제목 + 문서번호 + 상태 한 줄 요약 -->
      <div class="form-header">
        <h2>{{ isNew ? '지출결의서 작성' : '지출결의서 수정' }}</h2>
        <div class="form-header-meta">
          <span v-if="formData.document_number" class="document-number">{{ formData.document_number }}</span>
          <StatusDisplay :statusKey="formData.status"/>
        </div>
      </div>

      <!-- 상단: 지출결의서 기본 정보 -->
      <el-form
              :model="formData"
              :label-width="isMobile ? undefined : '120px'"
              :label-position="isMobile ? 'top' : 'right'"
              :rules="rules"
              ref="formRef"

      >

        <el-card shadow="hover">
          <h5>결제정보</h5>

        <el-row :gutter="20">
          <el-col :span="24" :md="6">
            <el-form-item label="결제 요청일" prop="payment_due_date">
              <el-date-picker
                v-model="formData.payment_due_date"
                type="date"
                placeholder="날짜 선택"
                style="width: 100%"
                :disabled="isReadOnly"
              ></el-date-picker>
            </el-form-item>
          </el-col>

          <el-col :span="24" :md="6">
            <el-form-item label="마감월" prop="close_month">
              <!-- 2026-08-18: 1~12 숫자 입력기 → 월 선택기(YYYY-MM).
                   숫자로 저장된 마감월("8")은 자재마감 리포트
                   (close_month="2026-08" 정확일치)에서 통째로 누락됐다. -->
              <el-date-picker
                v-model="formData.close_month"
                type="month"
                value-format="YYYY-MM"
                format="YYYY-MM"
                placeholder="마감월 선택"
                style="width: 100%"
                :disabled="isReadOnly"
              ></el-date-picker>
            </el-form-item>
          </el-col>

          <el-col :span="24" :md="6">
            <!-- 결제 방법 선택 필드 -->
              <PaymentMethodSelect
                v-model="formData.payment_method"
                :disabled="isReadOnly"
              />
          </el-col>
          <el-col :span="24" :md="6">
            <el-form-item label="발주사" prop="out_compay">

              <el-select v-model="formData.out_compay" style="width: 100%" placeholder="우리회사 선택" :disabled="isReadOnly">
                <el-option
                  v-for="company in codeStore.ourCompanys"
                  :key="company.id"
                  :label="company.name"
                  :value="company.id"
                />
              </el-select>

            </el-form-item>
          </el-col>
        </el-row>
      </el-card>

      <el-card shadow="hover" style="margin-top: 20px;">
        <h5>계좌정보</h5>

        <el-row :gutter="20">

          <el-col :span="24" :md="12">
            <!-- 비용구분 -->

              <el-form-item label="비용구분" prop="category">
                <el-select
                    v-model="formData.category"
                    placeholder="비용구분을 선택하세요"
                    style="width: 100%"
                    :disabled="isReadOnly"
                >
                    <!-- 기존 비용구분 옵션 -->
                    <el-option
                      v-for="method in codeStore.expenseCategories"
                      :key="method.id"
                      :label="method.name"
                      :value="method.id"
                    />
                    <!-- 비용구분 추가 옵션 -->
                    <!--
                    <el-option :value="'add_new'" :key="'add_new'" label="추가하기" />
                    -->
                </el-select>
                </el-form-item>
          </el-col>        

          <el-col :span="24" :md="12">
            <el-form-item label="거래처" prop="partner_company_name">
              <!-- 직접 입력이 아닌 검색 모달 트리거 — readonly + 돋보기로 의도 표시 -->
              <el-input
                v-model="formData.partner_company_name"
                placeholder="클릭하여 거래처 검색"
                readonly
                :disabled="isReadOnly"
                style="width: 100%"
                class="picker-input"
                @click="!isReadOnly && openSupplierModal()"
              >
                <template #suffix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">          

          <el-col :span="24" :md="6">
            <el-form-item label="거래은행" prop="bank">
              <el-input
                v-model="formData.bank"
                placeholder="거래은행"
                :disabled="isReadOnly"
                :maxlength="20" 
                 style="width: 100%"
              ></el-input>
            </el-form-item>
          </el-col>

          <el-col :span="24" :md="6">
            <el-form-item label="계좌번호" prop="account_number">
              <el-input
                v-model="formData.account_number"
                placeholder="계좌번호"
                :disabled="isReadOnly"
                :maxlength="20" 
                 style="width: 100%"
              ></el-input>              
            </el-form-item>
          </el-col>
          <el-col :span="24" :md="6">
            <el-form-item label="예금주" prop="account_holder">
              <el-input
                v-model="formData.account_holder"
                placeholder="예금주"
                :maxlength="20" 
                :disabled="isReadOnly"
                 style="width: 100%"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24" :md="6">
            <el-form-item label="SWIFT코드" prop="swift_code">
              <el-input
                v-model="formData.swift_code"
                placeholder="SWIFT코드"
                :disabled="isReadOnly"
                :maxlength="20" 
                 style="width: 100%"
              ></el-input>              
            </el-form-item>            
          </el-col>
        </el-row>
      </el-card>

      
      <el-card shadow="hover" style="margin-top: 20px;">
        <h5>금액정보</h5>
        <!-- 통화를 먼저 선택 → KRW 면 외화금액/환율은 접고 원화금액만 직접 입력 -->
        <el-row :gutter="20">
          <el-col :span="24" :md="6">
            <el-form-item label="통화" prop="currency">

                  <el-select v-model="formData.currency" placeholder="통화를 선택하세요" :disabled="isReadOnly">
                    <el-option
                      v-for="currencyOption in availableCurrencies"
                      :key="currencyOption.value"
                      :label="currencyOption.label"
                      :value="currencyOption.value" >
                    </el-option>
                  </el-select>
                </el-form-item>
          </el-col>
          <el-col v-if="formData.currency !== 'KRW'" :span="24" :md="6">
              <el-form-item label="외화금액" prop="amount_foreign">
                <el-input
                    v-model="formattedAmount"
                    label="외화금액"
                    @input="handleInput"
                    @blur="handleBlur"
                    :min="0"
                    :max="1000000000"

                    placeholder="외화금액 입력 "
                    :disabled="isReadOnly"
                  />
              </el-form-item>
          </el-col>
          <el-col v-if="formData.currency !== 'KRW'" :span="24" :md="6">
            <el-form-item label="적용환율" prop="fx_rate">
              <el-input-number

                :model-value="Number(formData.fx_rate)"
                @update:model-value="val => formData.fx_rate = val"
                :min="1"
                :max="2000"
                placeholder="0"
                :step="0.01"
                :disabled="isReadOnly"
              ></el-input-number>
            </el-form-item>
          </el-col>

          <el-col :span="24" :md="6">
            <el-form-item label="원화금액" prop="amount">
              <el-input
                  v-model="formData.amount"
                  label="원화금액"
                  :formatter="(value) => formatCurrency(value, 'KRW')"
                  :parser="parseCurrency"
                  :min="0"
                  :max="1000000000"
                  :step="0.1"
                  placeholder="원화 금액 입력 (최대 10억)"
                  :disabled="isReadOnly"
                />
              </el-form-item>
          </el-col>

          <!-- 부가세타입을 같은 행으로 (2026-08-01 그리드 정리 — KRW면 통화·원화금액·
               부가세타입 한 줄 완결, 외화면 자동 줄바꿈. 빈 우측 공간 제거) -->
          <el-col :span="24" :md="6">
               <el-form-item label="부가세타입">
                 <el-select v-model="formData.vat_type" :disabled="isReadOnly">
                   <el-option label="과세" value="01" />
                   <el-option label="면세" value="02" />
                   <el-option label="영세" value="03" />
                   <el-option label="원천징수(3.3%) — 개인/프리랜서" value="04" />
                 </el-select>
               </el-form-item>
               <!-- 프리랜서 지원 (2026-07-20): 원천징수 선택 시 공제·실지급 자동 표시 -->
               <div v-if="formData.vat_type === '04' && Number(formData.amount) > 0" class="withholding-hint">
                 공제 3.3%: <b>{{ withholdingTax.toLocaleString() }}원</b> →
                 실지급 <b>{{ netPayment.toLocaleString() }}원</b>
                 <span class="wh-sub">(회사가 원천징수·신고하는 건에만 선택 — 세무사 별도 처리 건은 기존 구분 유지)</span>
               </div>
         </el-col>
        </el-row>

      </el-card>

      <!-- 집행 근거 (2026-07-15) — 비용구분이 근거 유형을 결정:
           자재 계열 → 입고내역 / 임가공·물류·관세 등 → 작업지시 배분.
           비용구분 미매칭 시 둘 다 노출 (사이먼: 비용구분 기반 근거) -->
      <el-card shadow="hover" style="margin-top: 20px;">
        <div class="section-header">
          <h5>
            집행 근거
            <span class="evidence-hint" v-if="evidenceHint">{{ evidenceHint }}</span>
          </h5>
          <div class="section-header-right">
            <span v-if="formData.details?.length" class="details-sum" :class="{ 'details-sum--mismatch': detailsMismatch }">
              합계 {{ formatCurrency(detailsTotal, 'KRW') }}
              <template v-if="detailsMismatch"> · 원화금액과 불일치</template>
            </span>
            <el-button
              v-if="evidenceType === 'receiving' || evidenceType === 'any'"
              type="primary" plain size="small" @click="openReceivingModal()" :disabled="isReadOnly">
              입고내역 추가
            </el-button>
            <el-button
              v-if="evidenceType === 'allocation' || evidenceType === 'any'"
              type="warning" plain size="small" @click="openMrAllocate()" :disabled="isReadOnly">
              작업지시 배분 추가
            </el-button>
          </div>
        </div>
          <el-table v-if="formData.details?.length" :data="formData.details" style="width: 100%; margin-top: 8px">
            <el-table-column prop="style_number" label="스타일번호" />
            <el-table-column prop="color" label="색상" />
            <el-table-column prop="material_name" label="품명" />
            <el-table-column prop="specification" label="규격" />
            <el-table-column prop="quantity" label="수량" >
              <template #default="scope">
                <el-input v-model="scope.row.quantity" size="small" :disabled="isReadOnly" @input="recalcDetail(scope.row)" />
              </template>
            </el-table-column>

            <el-table-column prop="unit_price" label="단가" >
              <template #default="scope">
                <el-input v-model="scope.row.unit_price" size="small" :disabled="isReadOnly" @input="recalcDetail(scope.row)" />
              </template>
            </el-table-column>

            <el-table-column prop="amount" label="금액" >
              <template #default="scope">
                <el-input v-model="scope.row.amount" size="small" :disabled="isReadOnly" />
              </template>
            </el-table-column>


            <el-table-column prop="remark" label="비고" >
              <template #default="scope">
                <el-input v-model="scope.row.remark" size="small" :disabled="isReadOnly" />
              </template>
            </el-table-column>

            <el-table-column label="삭제" width="70">
              <template #default="scope">
                <el-button size="small" :disabled="isReadOnly" @click="removeReceiving(scope.$index)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div v-else class="empty-hint">등록된 입고내역이 없습니다.</div>
      </el-card>

      <!-- 증빙 첨부 (2026-07-17) — 종이 원본 관행 유지 + 시스템 사본 (선택) -->
      <el-card shadow="hover" style="margin-top: 20px;">
        <h5>증빙 첨부 <span class="att-hint">— 종이 원본과 별개의 시스템 사본 (선택, 과세 지결은 첨부 권장)</span></h5>
        <!-- 신규 작성 (2026-07-24 요청) — 저장 전 파일을 담아두면 저장 시 함께 업로드됩니다. -->
        <template v-if="!formData.id">
          <div class="att-upload-row">
            <el-select v-model="attForm.doc_type" size="small" style="width: 140px">
              <el-option label="세금계산서" value="TAX_INVOICE" />
              <el-option label="거래명세표" value="STATEMENT" />
              <el-option label="입고확인" value="RECEIVING" />
              <el-option label="영수증" value="RECEIPT" />
              <el-option label="계약/발주서" value="CONTRACT" />
              <el-option label="기타" value="OTHER" />
            </el-select>
            <input ref="pendingFileInput" type="file" style="font-size: 12px;" />
            <el-input v-model="attForm.memo" size="small" placeholder="비고 (선택)" style="width: 180px" />
            <el-button size="small" type="primary" @click="queueAttachment">담기</el-button>
            <span class="att-hint">저장 시 함께 업로드됩니다</span>
          </div>
          <el-table v-if="pendingAttachments.length" :data="pendingAttachments" size="small" style="margin-top: 8px">
            <el-table-column prop="doc_type_display" label="유형" width="110" />
            <el-table-column prop="name" label="파일" min-width="200" />
            <el-table-column prop="memo" label="비고" min-width="120" />
            <el-table-column label="" width="70">
              <template #default="{ $index }">
                <el-button size="small" text type="danger" @click="removePending($index)">제거</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div v-else class="att-hint" style="padding: 4px 0;">담아둔 증빙 없음</div>
        </template>
        <template v-else>
          <div class="att-upload-row">
            <el-select v-model="attForm.doc_type" size="small" style="width: 140px">
              <el-option label="세금계산서" value="TAX_INVOICE" />
              <el-option label="거래명세표" value="STATEMENT" />
              <el-option label="입고확인" value="RECEIVING" />
              <el-option label="영수증" value="RECEIPT" />
              <el-option label="계약/발주서" value="CONTRACT" />
              <el-option label="기타" value="OTHER" />
            </el-select>
            <input ref="attFileInput" type="file" style="font-size: 12px;" />
            <el-input v-model="attForm.memo" size="small" placeholder="비고 (선택)" style="width: 180px" />
            <el-button size="small" type="primary" :loading="attUploading" @click="uploadAttachment">업로드</el-button>
          </div>
          <el-table v-if="(formData.attachments || []).length" :data="formData.attachments" size="small" style="margin-top: 8px">
            <el-table-column prop="doc_type_display" label="유형" width="110" />
            <el-table-column label="파일" min-width="200">
              <template #default="{ row }">
                <a :href="row.file_url || row.file" target="_blank">{{ row.original_name || '파일' }}</a>
              </template>
            </el-table-column>
            <el-table-column prop="memo" label="비고" min-width="120" />
            <el-table-column prop="created_at" label="첨부일" width="150" />
            <el-table-column label="" width="70">
              <template #default="{ row }">
                <el-button size="small" text type="danger" @click="deleteAttachment(row)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div v-else class="att-hint" style="padding: 4px 0;">첨부된 증빙 없음</div>
        </template>
      </el-card>

      <!-- 비고 -->
      <el-card shadow="hover" style="margin-top: 20px;">
        <h5>비고</h5>
        <el-form-item label-width="0" prop="description">
          <el-input
            v-model="formData.description"
            :disabled="isReadOnly"
            :rows="4"
            maxlength="200"
            show-word-limit
            type="textarea"
          ></el-input>
        </el-form-item>
      </el-card>

          <!-- 작성 메타: 상태는 헤더 칩으로 노출, 여기는 작성일자만 -->
          <el-row :gutter="20">
                <el-col :span="12" :md="6">
                  <el-form-item label="작성일자">
                    <el-input
                      v-model="formData.created_at"
                      :disabled="true"
                      />
                  </el-form-item>
                </el-col>
          </el-row>


      </el-form>
    </el-card>



  </div>

  <!-- 액션 바: 모바일에서는 하단 고정 -->
  <div class="form-action-bar">
    <el-button @click="goBack">뒤로가기</el-button>
    <el-button v-if="canEdit" type="primary" class="action-primary" @click="saveForm">저장</el-button>
    <!-- 관리자 정정 (2026-08-03) — 제출 이후 문서의 오기입을 정정관리자가 수정.
         어드민 직접 수정 대체: 사유 필수, 감사이력 기록. -->
    <el-button v-if="canCorrect" type="warning" plain @click="openCorrection">관리자 정정</el-button>
  </div>

  <!-- 관리자 정정 다이얼로그 -->
  <el-dialog v-model="correction.visible" title="지출결의서 관리자 정정" width="560px">
    <el-alert type="warning" :closable="false" show-icon class="mb-3"
      title="접수·집행 후 문서도 수정됩니다. 변경 내용과 사유는 감사이력에 기록되며, 원가 집계에 즉시 반영됩니다." />
    <div class="corr-grid">
      <label>원화 금액</label>
      <el-input-number v-model="correction.form.amount" :min="0" :controls="false" style="width: 100%" />
      <label>마감월</label>
      <el-date-picker v-model="correction.form.close_month" type="month"
        value-format="YYYY-MM" style="width: 100%" />
      <label>비용구분</label>
      <el-select v-model="correction.form.category" filterable style="width: 100%">
        <el-option v-for="c in codeStore.expenseCategories" :key="c.id" :label="c.name" :value="c.id" />
      </el-select>
      <label>거래처</label>
      <el-select v-model="correction.form.partner_company" filterable style="width: 100%">
        <el-option v-for="s in codeStore.suppliers" :key="s.supplier_id || s.id"
          :label="s.name" :value="s.supplier_id || s.id" />
      </el-select>
      <label>부가세구분</label>
      <el-select v-model="correction.form.vat_type" style="width: 100%">
        <el-option label="과세" value="01" />
        <el-option label="면세" value="02" />
        <el-option label="영세" value="03" />
        <el-option label="원천징수(3.3%)" value="04" />
      </el-select>
      <label>결제요청일</label>
      <el-date-picker v-model="correction.form.payment_due_date" type="date"
        value-format="YYYY-MM-DD" style="width: 100%" />
      <label>계좌번호</label>
      <el-input v-model="correction.form.account_number" />
      <label>예금주</label>
      <el-input v-model="correction.form.account_holder" />
      <label>비고</label>
      <el-input v-model="correction.form.description" type="textarea" :rows="2" />
    </div>
    <el-input v-model="correction.reason" type="textarea" :rows="2" class="mt-2"
      placeholder="정정 사유 (필수) — 예: 금액 오기입 100만→120만" />
    <template #footer>
      <el-button @click="correction.visible = false">닫기</el-button>
      <el-button type="warning" :loading="correction.saving" @click="submitCorrection">정정 저장</el-button>
    </template>
  </el-dialog>

  <div>
    <!-- 거래처 모달 -->
        <SupplierSelectModal
          v-if="showSupplierModal"
          :suppliers="suppliers"
          @close="closeSupplierModal"
          @select="handleSupplierSelect"
        />



    <!-- 입고내역모달 -->
        <!-- 작업지시 배분 다이얼로그 (2026-07-15) -->
    <el-dialog v-model="mrAllocVisible" title="작업지시 배분 추가" width="520px">
      <div class="mr-alloc-hint">
        입고내역이 없는 비용(임가공임 등)을 메인오더(스타일)별로 나눠 귀속합니다.
        금액은 추가 후 표에서 수정할 수 있습니다.
      </div>
      <el-form label-width="90px" style="margin-top:10px">
        <el-form-item label="메인오더">
          <el-select
            v-model="mrAllocId" filterable clearable style="width:100%"
            placeholder="지시서번호 / 브랜드 / 스타일 검색"
          >
            <el-option
              v-for="mr in mrOptions" :key="mr.id"
              :label="`${mr.request_number} - ${mr.brand_name || ''} / ${mr.design_style_number || ''}`"
              :value="mr.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="금액">
          <el-input v-model="mrAllocAmount" style="width:200px" placeholder="배분 금액" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="mrAllocVisible = false">취소</el-button>
        <el-button type="primary" :disabled="!mrAllocId" @click="confirmMrAllocate">추가</el-button>
      </template>
    </el-dialog>

    <ReceivingSelector 
            v-model="showReceivingModal"            
            :partnerCompanyId="formData.partner_company"
            @select="handleReceivingSelect" />
  </div>
</template>

<script setup>
    import { computed,ref,reactive,onMounted ,onBeforeUnmount, onActivated, watch  } from "vue";
    import { useExpenseStore } from "@/stores/expenseStore";
    import { useRoute } from "vue-router";
    import { useTabsStore } from "@/stores/tabsStore";
    import { useCodeStore } from "@/stores/codeStore"; // Pinia 스토어 가져오기
    import { useUserStore } from "@/stores/user"; // 관리자 정정 권한 확인용 (2026-08-03)
    import axiosInstance from '@/api/axios';
    import { ElMessage, ElMessageBox } from 'element-plus'; // 메시지 박스와 메시지 컴포넌트
    import StatusDisplay from "@/components/StatusDisplay.vue";
    import dayjs from "dayjs";
    import PaymentMethodSelect from '@/components/PaymentMethodSelect.vue';
    import SupplierSelectModal from './SupplierSelectModal.vue';
    import { formatCurrency } from "@/utils/currency";
    import { useRouter } from "vue-router";
    import ReceivingSelector from '@/components/ReceivingSelector.vue';
    import { Search } from '@element-plus/icons-vue';
    
    // 모바일 분기 (768px 기준) — 라벨 위치/하단 고정 액션바 전환용
    const isMobile = ref(window.innerWidth <= 768);
    const onResize = () => { isMobile.value = window.innerWidth <= 768; };
    onMounted(() => window.addEventListener('resize', onResize));
    onBeforeUnmount(() => window.removeEventListener('resize', onResize));


    //Pina 스토어 사용
   const codeStore = useCodeStore();
   const router = useRouter();
   const tabsStore = useTabsStore();
   const formRef = ref([])
   const formData = reactive({  
      account_number:'',
      account_holder:null,
      amount_foreign:0,
      amount:0,
      amount_tax: 0,
      close_month:'',
      category :'',
      currency:'KRW',
      description:'',
      document_number:'',
      fx_rate:1,
      id:null,            
      is_creator:false,
      out_compay:'',

      payment_due_date:'',
      partner_company:'',
      partner_company_name:'',
      payment_method:'',

      status:'draft',
      swift_code:'',
      vat_type:'01',
      //created_by_name:'',
      created_at:null,   
      bank:'',
      //tax_invoice:'',
      //business_registration:'', 
      

});
// 신규 작성 초기값 스냅샷 — 저장 후 폼 리셋용 (아래 resetNewForm)
const FORM_DEFAULTS = JSON.parse(JSON.stringify(formData));
// 전체 통화 목록
const availableCurrencies = ref([
        {value:"KRW",label:"KRW"},
        {value:"USD",label:"USD"},
        {value:"EUR",label:"EUR"},
        {value:"JPY",label:"JPY"}
      ]  
  );

  // 상태 관리
const formattedAmount = ref(formatCurrency(formData.amount_foreign, formData.currency));
// 입력 이벤트 핸들러
const handleInput = (value) => {
  const numericValue = parseCurrency(value);

  
  // 소수점 2자리까지만 허용
  const limitedValue = limitToTwoDecimalPlaces(numericValue);

  formattedAmount.value = limitedValue; // 입력 중에는 포맷 적용 안 함
  formData.amount_foreign = numericValue; // 원본 값 업데이트
};

// 블러 이벤트 핸들러
const handleBlur = () => {
  formattedAmount.value = formatCurrency(formData.amount_foreign, formData.currency);
};

// 소수점 2자리 제한 함수
function limitToTwoDecimalPlaces(value) {
  const regex = /^\d+(\.\d{0,2})?$/; // 정규식: 정수 또는 소수점 이하 2자리까지 허용
  const stringValue = value.toString();

  // 유효하지 않은 경우 마지막 두 자리를 잘라내기
  if (!regex.test(stringValue)) {
    return parseFloat(stringValue).toFixed(2);
  }
  return value;
}


// 결제 방법에 따른 통화 옵션 업데이트
const updateCurrencyOptions = () => {  
  console.log('before availableCurrencies:',availableCurrencies)
  console.log(typeof formData.payment_method); // 타입 확인
  console.log(formData.payment_method);        // 실제 값 출력

  //LC ,TT
  if ( formData.payment_method === 3  || formData.payment_method === 6 ) {
    console.log('111    formData.payment_method:',formData.payment_method)
  
    availableCurrencies.value =[
        {value:"USD",label:"USD"},
        {value:"EUR",label:"EUR"},
        {value:"JPY",label:"JPY"}
      ]  ; // L/C나 T/T일 경우 원화 제외    

    if (!availableCurrencies.value.includes(formData.currency)) {
      formData.currency = "USD"; // 기본값 설정
    }

  } else {
    console.log('2222 formData.payment_method:',formData.payment_method)

    availableCurrencies.value = [
        {value:"KRW",label:"KRW"},
        {value:"USD",label:"USD"},
        {value:"EUR",label:"EUR"},
        {value:"JPY",label:"JPY"}
      ]  ; // 원화 포함
    //if (!availableCurrencies.value.includes(formData.currency)) {
    //  console.log("기본 ccy 설정 ")
    //  formData.currency = "KRW"; // 기본값 설정
    //}
  }

  console.log('after abalible currencies -=',availableCurrencies.value)

};

const showSupplierModal = ref(false);

const openSupplierModal = () => {
  showSupplierModal.value = true;
};

const closeSupplierModal = () => {
  showSupplierModal.value = false;
  console.log(" closeSupplierModal")
};

const handleSupplierSelect = (supplier,account) => {
  console.log("handleSupplierSelected supplier_id ", supplier.supplier_id )
  console.log("handleSupplierSelected name ", supplier.name )

  formData.partner_company = supplier.supplier_id;
  formData.partner_company_name = supplier.name;
  formData.account_number = account.account_number;
  formData.account_holder = account.account_holder;
  formData.bank = account.bank_name;
  formData.swift_code = account.swift_code;


  closeSupplierModal();
};

// 지출결의상세 
formData.details = ref([]); // 지출결의상세  입고 내역에서 가져옴 

const showReceivingModal = ref(false);

function openReceivingModal(){
  showReceivingModal.value=true
  console.log("showReceivingModal = ",showReceivingModal.value)
}

function handleReceivingSelect(selectedItems) {

  selectedItems.forEach(item => {
    // 이미 추가된 receiving_id는 중복 방지
    const exists = formData.details.some(r => r.receiving === item.id)


    if (!exists) {
      // 2026-07-15 발주↔지결 연결: 응답 필드(item_*)로 매핑 정정
      formData.details.push({
        receiving: item.id,
        style_number: item.style_number || '',
        color: item.item_color || '',
        material_name: item.item_name || '',
        specification: item.item_specification || '',
        quantity: item.quantity || 0,
        unit_price: item.unit_price || 0,
        amount: (item.quantity || 0) * (item.unit_price || 0),
        remark: '',
      })
      console.log(formData.details)
    }else {
      // ✅ 이미 추가된 경우 메시지 띄우기
      ElMessage.warning(`스타일번호: ${item.style_number || 'N/A'} 자재가 이미 추가되었습니다.`);
    }
  })
}

function removeReceiving(index) {
  formData.details.splice(index, 1);
}

// ── 집행 근거 유형 (2026-07-15) — 비용구분(ExpenseCategory)이 결정 ──
// 색상·월마감과 동일 원칙: 이름 키워드 매핑, 미매칭이면 'any'(둘 다 노출).
// 정식화(비용구분 마스터에 근거유형 필드)는 운영 피드백 후.
const categoryNameOf = (id) =>
  codeStore.expenseCategories?.find?.(c => c.id === id)?.name || ''

// 비용구분 마스터의 원가유형 (2026-07-17, F12 — 서버 정식 매핑)
const categoryCostTypeOf = (id) =>
  codeStore.expenseCategories?.find?.(c => c.id === id)?.cost_type || ''

const evidenceType = computed(() => {
  // 1순위: 마스터의 원가유형 (F12)
  const ct = categoryCostTypeOf(formData.category)
  if (ct === 'MATERIAL' || ct === 'SUBSIDIARY') return 'receiving'
  if (['PROCESSING', 'SHIPPING', 'CUSTOMS', 'INSPECTION'].includes(ct)) return 'allocation'
  // 2순위(fallback): 이름 키워드 — cost_type 미지정 비용구분 호환
  const name = categoryNameOf(formData.category)
  if (/자재|원단|원부|부자재/.test(name)) return 'receiving'
  if (/임가공|가공|물류|운송|해운|항공|관세|통관|검품|선적/.test(name)) return 'allocation'
  return 'any'
})

const evidenceHint = computed(() => {
  const name = categoryNameOf(formData.category)
  if (!name) return ''
  if (evidenceType.value === 'receiving') return `비용구분 '${name}' → 근거: 입고내역`
  if (evidenceType.value === 'allocation') return `비용구분 '${name}' → 근거: 작업지시 배분`
  return ''
})

// 비용구분 → 원가 카테고리 유형 (배분 행 자동 태깅)
// 1순위: 마스터 cost_type (F12), 2순위: 이름 키워드 fallback.
// 서버(_save_details)도 동일 규칙으로 자동 태깅하므로 여기서 못 정해도 안전.
const costTypeForCategory = (name) => {
  const ct = categoryCostTypeOf(formData.category)
  if (ct) return ct
  if (/임가공|가공/.test(name)) return 'PROCESSING'
  if (/물류|운송|해운|항공|선적|탑차|택배/.test(name)) return 'SHIPPING'
  if (/관세|통관/.test(name)) return 'CUSTOMS'
  if (/검품/.test(name)) return 'INSPECTION'
  return null
}

// ── 작업지시 배분 (2026-07-15) ──
const mrAllocVisible = ref(false)
const mrAllocId = ref(null)
const mrAllocAmount = ref('')
const mrOptions = ref([])
const costCategories = ref([])  // 원가 카테고리 목록 (배분 행 자동 태깅용)

async function openMrAllocate() {
  mrAllocId.value = null
  mrAllocAmount.value = ''
  mrAllocVisible.value = true
  if (!mrOptions.value.length) {
    try {
      const res = await axiosInstance.get('/production/main-requests/?ordering=-created_at')
      mrOptions.value = res.data.results || res.data || []
    } catch (e) { console.error('메인오더 목록 조회 실패:', e) }
  }
  if (!costCategories.value.length) {
    try {
      const res = await axiosInstance.get('/cost-categories/')
      costCategories.value = res.data.results || res.data || []
    } catch (e) { costCategories.value = [] }
  }
}

function confirmMrAllocate() {
  const mr = mrOptions.value.find(m => m.id === mrAllocId.value)
  if (!mr) return
  // 비용구분 → 원가 카테고리 유형 자동 태깅 (임가공→가공비, 물류→운송비,
  // 관세→관세/통관비, 검품→검품비) — 원가 비교의 해당 항목으로 집계
  const wantType = costTypeForCategory(categoryNameOf(formData.category))
  const cat = wantType
    ? costCategories.value.find(c => c.category_type === wantType)
    : null
  formData.details.push({
    main_request: mr.id,
    style_number: mr.design_style_number || '',
    color: '',
    material_name: categoryNameOf(formData.category) || '작업지시 배분',
    specification: mr.brand_name || '',
    quantity: 0,
    unit_price: 0,
    amount: Number(mrAllocAmount.value) || 0,
    remark: '',
    cost_category: cat ? cat.id : null,
  })
  mrAllocVisible.value = false
}

// 수량/단가 입력 시 금액 자동 계산 (금액 직접 수정도 허용)
function recalcDetail(row) {
  row.amount = (Number(row.quantity) || 0) * (Number(row.unit_price) || 0);
}

// 입고내역 합계 — 헤더 원화금액과 다르면 경고 표시
const detailsTotal = computed(() =>
  (formData.details || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
);
// 원천징수(3.3%) 파생 금액 (2026-07-20, 프리랜서 지원) — 서버 계산과 동일 규칙
const withholdingTax = computed(() => Math.round((Number(formData.amount) || 0) * 0.033));
const netPayment = computed(() => (Number(formData.amount) || 0) - withholdingTax.value);
const detailsMismatch = computed(() =>
  (formData.details || []).length > 0 &&
  Math.round(detailsTotal.value) !== Math.round(Number(formData.amount) || 0)
);


const rules = ref({
     
  payment_due_date: [
        { required: true, message: '결제 요청일을 선택하세요.', trigger: 'change' },
        {
          validator: (rule, value, callback) => {
            if (!value) {
              callback(new Error('결제 요청일을 선택하세요.'));
            } //else if (new Date(value) < new Date()) {
              //callback(new Error('결제 요청일은 오늘 이후여야 합니다.'));
              //} 
            else {
              callback();
            }
          },
          trigger: 'change',
        },
      ],

	close_month: [
        { required: true, message: '마감월을 입력하세요.', trigger: 'change' },
      ],
  payment_method: [
        { required: true, message: '결제 방법을 선택하세요.', trigger: 'change' },
      ],	  
	out_compay: [
        { required: true, message: '발주사를 입력하세요.', trigger: 'change' },
      ],	  
	partner_company_name:[
          { required: true, message: '거래처를 입력하세요.', trigger: 'blur' },
      ],
	bank:[
          { required: true, message: '거래은행을 입력하세요.', trigger: 'blur' },
      ],
	account_number:[
          { required: true, message: '계좌번호를  입력하세요.', trigger: 'blur' },
      ],
	account_holder:[
          { required: true, message: '예금주를  입력하세요.', trigger: 'blur' },
      ],	  
	amount_foreign:[
          { required: true, message: '외화금액을  입력하세요.', trigger: 'blur' },
      ],  
	currency:[
          { required: true, message: '통화를  입력하세요.', trigger: 'blur' },
      ],  
	fx_rate:[
          { required: true, message: '적용환율을  입력하세요.', trigger: 'blur' },
      ],
  amount: [
        { required: true, message: '금액을 입력하세요.', trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (value <= 0) {
              callback(new Error('금액은 0보다 커야 합니다.'));
            } else {
              callback();
            }
          },
          trigger: 'blur',
        },
      ],
  
  category: [
        { required: true, message: '비용구분을 선택하세요.', trigger: 'change' },
      ],
  description: [
        { max: 200, message: '비고는 최대 200자까지 입력할 수 있습니다.', trigger: 'blur' },
      ],
    });
   
    
    onMounted(async () => {
      try{

        codeStore.fetchOurCompanys();
        codeStore.fetchExpenseCategories();

        const id = route.params.id;   
        formData.created_at = dayjs().format("YYYY-MM-DD");

        if(id){        
          const response   = await axiosInstance.get(`expenses/${id}/`);
          //Object.assign(formData, response.data); // 속성을 병합하여 업데이트
          Object.entries(response.data).forEach(([key, value]) => {
            formData[key] = value; // 개별적으로 Vue 반응성 유지
          });
            
          // 외화 금액 초기화
          formattedAmount.value = formatCurrency(formData.amount_foreign, formData.currency);
          console.log("서버로부터 받은 통화 코드:", formData.currency); // 디버그 로그

          console.log(formData )
        }

        // 탭 부제 — 수정: 문서번호 / 신규: '새 문서' (동일 제목 탭 구분, 2026-08-01)
        tabsStore.setSubtitle(route.fullPath, id ? (formData.document_number || `#${id}`) : '새 문서');

      
      }catch(error){
        console.log(error)
      }finally{
        console.log(" 패치완료")
      }
      
    });

    const route = useRoute();

    // 읽기전용 판정: 신규 작성이거나, 본인이 작성한 작성중(draft) 문서만 수정 가능.
    // (과거 userRole 하드코딩 + calculateAccess 는 항상 작성자 기준으로 동작해 무력화돼 있었음 — 2026-06-11 정리)
    const isReadOnly = computed(() => !canEdit.value);

/*

    function handleSelect(item) {
      // Pinia 스토어에서 선택한 거래처 데이터 조회
      const selectedSpplier = codeStore.suppliers.find(        
        (supplier) => {         
          return String(supplier.name).toLowerCase() === String(item.value).toLowerCase();
        }       
      );

      if (selectedSpplier) {  
        formData.value.partner_company = selectedSpplier.supplier_id
        formData.value.account_number = selectedSpplier.account_number; // 계좌번호 자동 입력
        formData.value.account_holder = selectedSpplier.account_holder; // 예금주 자동 입력  
      }
    }

*/
        // 상태 계산
  const status = computed(() => formData.status);
  const isNew = computed(() => !formData.id); // ID가 없으면 신규 생성

    // 버튼 활성화 조건
    const canEdit = computed(() => isNew.value || formData.is_creator && ['draft'].includes(status.value));

    // ── 관리자 정정 (2026-08-03) — 정정관리자 그룹(슈퍼유저 포함), 제출 이후 문서만
    const userStore = useUserStore();
    const canCorrect = computed(() =>
      !isNew.value && status.value !== 'draft' && userStore.hasGroup('정정관리자'));

    const correction = reactive({
      visible: false, saving: false, reason: '',
      form: {}, snapshot: {},
    });

    const CORRECTION_FIELDS = ['amount', 'close_month', 'category', 'partner_company',
      'vat_type', 'payment_due_date', 'account_number', 'account_holder', 'description'];

    const openCorrection = () => {
      if (!codeStore.suppliers?.length) codeStore.fetchSuppliers?.();  // 거래처 셀렉트용
      const snap = {};
      CORRECTION_FIELDS.forEach(f => { snap[f] = formData[f]; });
      correction.snapshot = { ...snap };
      correction.form = { ...snap, amount: Number(snap.amount) || 0 };
      correction.reason = '';
      correction.visible = true;
    };

    const submitCorrection = async () => {
      if (!correction.reason.trim()) {
        ElMessage.warning('정정 사유를 입력하세요.');
        return;
      }
      // 바뀐 필드만 전송 — 서버 감사이력(before/after)이 깔끔해진다
      const body = { reason: correction.reason };
      let changed = 0;
      CORRECTION_FIELDS.forEach(f => {
        if (String(correction.form[f] ?? '') !== String(correction.snapshot[f] ?? '')) {
          body[f] = correction.form[f];
          changed += 1;
        }
      });
      if (!changed) {
        ElMessage.warning('변경된 내용이 없습니다.');
        return;
      }
      correction.saving = true;
      try {
        const { data } = await axiosInstance.post(`expenses/${formData.id}/correct/`, body);
        ElMessage.success(data.message || '정정되었습니다.');
        correction.visible = false;
        // 화면 재조회 (개별 대입 — Vue 반응성 유지, onMounted 로드와 동일 방식)
        const res = await axiosInstance.get(`expenses/${formData.id}/`);
        Object.entries(res.data).forEach(([key, value]) => { formData[key] = value; });
      } catch (err) {
        const d = err?.response?.data;
        const msg = typeof d === 'string' ? d
          : d?.error || d?.detail || Object.values(d || {})[0] || '정정에 실패했습니다.';
        ElMessage.error(Array.isArray(msg) ? msg[0] : msg);
      } finally {
        correction.saving = false;
      }
    };
    //const canSubmit = computed(() => isNew.value || formData.is_creator && ['draft'].includes(status.value));


  // 이벤트 처리
  // ── 증빙 첨부 (2026-07-17) ──
  const attFileInput = ref(null);
  const attUploading = ref(false);
  const attForm = reactive({ doc_type: 'TAX_INVOICE', memo: '' });

  // ── 신규 작성 중 첨부 큐 (2026-07-24 요청) — 저장 전 담아두고 저장 직후 일괄 업로드 ──
  const DOC_TYPE_LABELS = {
    TAX_INVOICE: '세금계산서', STATEMENT: '거래명세표', RECEIVING: '입고확인',
    RECEIPT: '영수증', CONTRACT: '계약/발주서', OTHER: '기타',
  };
  const pendingFileInput = ref(null);
  const pendingAttachments = ref([]);

  const queueAttachment = () => {
    const f = pendingFileInput.value?.files?.[0];
    if (!f) { ElMessage.warning('파일을 선택하세요.'); return; }
    pendingAttachments.value.push({
      file: f,
      name: f.name,
      doc_type: attForm.doc_type,
      doc_type_display: DOC_TYPE_LABELS[attForm.doc_type] || attForm.doc_type,
      memo: attForm.memo || '',
    });
    attForm.memo = '';
    if (pendingFileInput.value) pendingFileInput.value.value = '';
  };

  const removePending = (idx) => {
    pendingAttachments.value.splice(idx, 1);
  };

  // '추가' 버튼으로 다시 들어온 경우 초안 비우기 (2026-11-03 사이먼 확인):
  // keep-alive 캐시 인스턴스는 onMounted 가 다시 돌지 않으므로, 목록의 '추가' 가 올린
  // newFormGen 을 보고 "새 작성 요청"인지 판단한다. 단순 탭 복귀(값 그대로)는 초안 보존.
  const expenseStore = useExpenseStore();
  let seenNewFormGen = expenseStore.newFormGen;
  onActivated(() => {
    if (!route.params.id && expenseStore.newFormGen !== seenNewFormGen) {
      resetNewForm();
    }
    seenNewFormGen = expenseStore.newFormGen;
  });

  // 신규 저장 직후 폼을 초기 상태로 되돌린다 (2026-11-03 제보 서효정).
  // App.vue 의 <keep-alive> 가 /expenses/new 인스턴스를 fullPath 키로 캐시하므로
  // 다음 '추가' 때 onMounted 가 다시 돌지 않는다 — 이전 지결 값이 그대로 남아
  // 중복 작성되던 문제. 이동 전에 여기서 비워 두면 캐시 인스턴스가 깨끗하게 남는다.
  const resetNewForm = () => {
    Object.keys(FORM_DEFAULTS).forEach((k) => { formData[k] = FORM_DEFAULTS[k]; });
    formData.created_at = dayjs().format("YYYY-MM-DD");
    formattedAmount.value = formatCurrency(formData.amount_foreign, formData.currency);
    pendingAttachments.value = [];
    attForm.memo = '';
    if (pendingFileInput.value) pendingFileInput.value.value = '';
    formRef.value?.clearValidate?.();
  };

  // 저장으로 생성된 지결(expenseId)에 담아둔 첨부를 업로드. 실패 건은 건너뛰고 경고.
  const flushPendingAttachments = async (expenseId) => {
    if (!expenseId || !pendingAttachments.value.length) return;
    let failed = 0;
    for (const p of pendingAttachments.value) {
      try {
        const fd = new FormData();
        fd.append('file', p.file);
        fd.append('doc_type', p.doc_type);
        fd.append('memo', p.memo || '');
        await axiosInstance.post(`expenses/${expenseId}/attachments/`, fd);
      } catch (e) { console.error('첨부 업로드 실패', p.name, e); failed++; }
    }
    if (failed) ElMessage.warning(`증빙 ${failed}건 업로드에 실패했습니다. 상세 화면에서 다시 첨부해 주세요.`);
    pendingAttachments.value = [];
  };

  const refreshAttachments = async () => {
    if (!formData.id) return;
    try {
      const res = await axiosInstance.get(`expenses/${formData.id}/`);
      formData.attachments = res.data.attachments || [];
    } catch (e) { console.error(e); }
  };

  const uploadAttachment = async () => {
    const f = attFileInput.value?.files?.[0];
    if (!f) { ElMessage.warning('파일을 선택하세요.'); return; }
    attUploading.value = true;
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('doc_type', attForm.doc_type);
      fd.append('memo', attForm.memo || '');
      await axiosInstance.post(`expenses/${formData.id}/attachments/`, fd);
      ElMessage.success('증빙이 첨부되었습니다.');
      attForm.memo = '';
      if (attFileInput.value) attFileInput.value.value = '';
      await refreshAttachments();
    } catch (e) {
      ElMessage.error('첨부 실패: ' + (e.response?.data?.detail || e.message));
    } finally {
      attUploading.value = false;
    }
  };

  const deleteAttachment = async (row) => {
    try {
      await ElMessageBox.confirm('이 증빙을 삭제하시겠습니까?', '삭제', { type: 'warning' });
      await axiosInstance.delete(`expenses/${formData.id}/attachments/${row.id}/`);
      ElMessage.success('삭제되었습니다.');
      await refreshAttachments();
    } catch (e) { if (e !== 'cancel') ElMessage.error('삭제 실패'); }
  };

  // ── 지결 크로스체크 (2026-07-17) — 저장 전 중복지출/견적대비/근거 검사 ──
  const runCrossCheck = async () => {
    try {
      const res = await axiosInstance.post('expenses/cross-check/', {
        id: formData.id || null,
        partner_company: formData.partner_company,
        category: formData.category,
        amount: formData.amount,
        close_month: formData.close_month,
        vat_type: formData.vat_type,
        details: (formData.details || []).map(d => ({
          receiving: d.receiving || null,
          main_request: d.main_request || null,
          cost_category: d.cost_category || null,
          quantity: d.quantity || 0,
          amount: d.amount || 0,
        })),
      });
      return res.data;
    } catch (e) {
      console.error('cross-check failed:', e);
      return { errors: [], warnings: [], budget: [] }; // 검사 실패가 저장을 막지 않게
    }
  };

  const formatCheckHtml = (cc) => {
    const esc = (t) => String(t).replace(/</g, '&lt;');
    let html = '';
    if (cc.errors.length) {
      html += '<div style="color:#f56c6c;font-weight:700;margin-bottom:4px;">차단 사유</div>'
        + cc.errors.map(e => `<div style="margin-bottom:3px;">✗ ${esc(e.message)}</div>`).join('');
    }
    if (cc.warnings.length) {
      html += '<div style="color:#e6a23c;font-weight:700;margin:6px 0 4px;">경고</div>'
        + cc.warnings.map(w => `<div style="margin-bottom:3px;">⚠ ${esc(w.message)}</div>`).join('');
    }
    if (cc.budget.length) {
      html += '<div style="font-weight:700;margin:6px 0 4px;">견적 대비 현황</div>'
        + cc.budget.map(b =>
          `<div style="margin-bottom:2px;">${esc(b.request_number)} ${esc(b.bucket)}: `
          + `누계 ${Number(b.after).toLocaleString()}원 / 견적 ${Number(b.budget).toLocaleString()}원`
          + (b.rate != null ? ` (${b.rate}%)` : '') + '</div>').join('');
    }
    return `<div style="max-height:320px;overflow:auto;font-size:13px;text-align:left;">${html}</div>`;
  };

  const saveForm = async () => {
  try {
        await formRef.value.validate();

        // 크로스체크 — 오류는 차단, 경고/예산 초과는 확인 후 진행
        const cc = await runCrossCheck();
        if (cc.errors.length) {
          await ElMessageBox.alert(formatCheckHtml(cc), '지출결의 크로스체크 — 저장 불가', {
            dangerouslyUseHTMLString: true, confirmButtonText: '확인',
          });
          return;
        }
        if (cc.warnings.length || cc.budget.some(b => b.rate != null && b.rate > 100)) {
          try {
            await ElMessageBox.confirm(formatCheckHtml(cc), '지출결의 크로스체크 — 확인 필요', {
              dangerouslyUseHTMLString: true,
              confirmButtonText: '확인 후 저장', cancelButtonText: '취소',
              type: 'warning',
            });
          } catch { return; }
        }
        //날자 포멧팅
        const formattedDate = dayjs(formData.payment_due_date).format("YYYY-MM-DD");
        formData.payment_due_date = formattedDate
        //delete  formData.created_at 

        //PayLoad 생성
        const payload = {
                        //...toRaw(formData),
                        account_number: formData.account_number,
                        account_holder: formData.account_holder,
                        amount: formData.amount,
                        amount_foreign: formData.amount_foreign,
                        bank: formData.bank,
                        currency: formData.currency,
                        category: formData.category,
                        close_month: formData.close_month,
                        created_at: formData.created_at,
                        description: formData.description,
                        document_number: formData.document_number,
                        fx_rate: formData.fx_rate,
                        id: formData.id,
                        is_creator: formData.is_creator,
                        out_compay: formData.out_compay,
                        partner_company: formData.partner_company,
                        payment_method: formData.payment_method,
                        payment_due_date: formData.payment_due_date,
                        status: formData.status,
                        swift_code: formData.swift_code,
                        vat_type: formData.vat_type,           

                        // 필요한 추가 필드가 있으면 여기 추가
                        // 2026-07-15: 서버가 details 를 실제 저장 (그동안 버려졌음)
                        // — 세액은 서버가 부가세구분(vat_type)으로 산출
                        details: formData.details.map(item => ({
                          receiving: item.receiving,
                          main_request: item.main_request,
                          cost_category: item.cost_category,
                          quantity: item.quantity,
                          unit_price: item.unit_price,
                          amount: item.amount,
                          remark: item.remark || '',
                      }))
        };                    
        console.log(payload)

        //저장 또는 추가 
        if (formData.id) {
             // 수정
             await axiosInstance.put(`expenses/${formData.id}/`, payload);     
             //ElMessage.success("저장 하였습니다.")
             ElMessage({
                message: "저장 하였습니다.",
                type: "success",
                duration: 3000, // 메시지 표시 시간
              });
        } else {
             // 추가
             console.log('formData.value',formData)
             const response = await axiosInstance.post("expenses/", payload);
             console.log("추가완료")
             ElMessage.success("신규 생성 하였습니다.")

              // 생성된 ID를 가져옵니다.
            const newId = response.data.id;

            // 담아둔 증빙 첨부 업로드 (2026-07-24 요청) — 이동 전에 처리
            await flushPendingAttachments(newId);

            // 캐시된 신규 폼 비우기 — 다음 '추가' 에 이전 값이 끌려오지 않게
            resetNewForm();

            // 상세 화면으로 이동
            if (newId) {
                router.push(`/expenses/${newId}`);

            }

        }


    } catch (error) {
      console.log(error)
      //ElMessage.error(" 저장 중 오류 발생:", error);

      if (error.response) {
        // 서버에서 반환된 오류 메시지 출력
        ElMessage.error(error.response.data.message || "저장 중 오류가 발생했습니다.");
      } else {
        // 네트워크 오류 등
        ElMessage.error("저장 중 오류가 발생했습니다. 네트워크를 확인해주세요.");
      }
    }
};

/* 신규작성에는 상태 변경이 필요 없어

const warnings = {
  submitted: "제출 후에는 수정이 불가능합니다. 계속하시겠습니까?",
  received: "경리과 접수 상태로 변경됩니다. 계속하시겠습니까?",
  completed: "집행 완료 후에는 변경할 수 없습니다. 계속하시겠습니까?",
};

const changeStatus = async (newStatus) => {
  try {

    //if (confirm("상태를 변경하시겠습니까?")) {
    if (confirm(warnings[newStatus])) {
      // API 호출 로직
      const response = await axiosInstance.post(`expenses/${formData.id}/change-status/`, {
        new_status: newStatus,
      });
      formData.status = response.data.new_status;
      ElMessage.success(`상태가 ${newStatus}로 변경됩니다.`);

    } else {
      ElMessage.info("상태 변경이 취소되었습니다.");
    }   
  } catch (error) {
    ElMessage.error("상태 변경 중 오류가 발생했습니다.");
  }
};
*/

/*
const formatCurrency=(value)=> {
      console.log('formatcurrency:',value)

      if (!value && value !== 0) return ''; // 빈 값 처리
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','); // 천단위 콤마 추가
}
      */

const parseCurrency=(value) =>{
      if (!value) return 0; // 빈 값 처리
      //return parseFloat(value.replace(/,/g, '')); // 천단위 콤마 제거 후 숫자로 변환
      var returnvalue = value.toString().replace(/[^0-9.]/g, '');

      // 소수점 이하 2자리 제한
      const parts = returnvalue.split('.');
      if (parts.length > 1) {
        parts[1] = parts[1].substring(0, 2); // 소수점 이하 2자리만 유지
        returnvalue = parts.join('.');
      }



      console.log("parseCurrency return value:",returnvalue)
      return returnvalue;

}


  // 금액 자동 계산
  // - KRW: 원화금액 직접 입력, 환율 1 고정, 외화금액은 원화와 동기화(데이터 정합)
  // - 외화: 외화금액/환율 변경 시 원화금액 = 외화금액 × 환율 (소수점 2자리) 자동 계산.
  //   단, 자동 계산 후 원화금액 직접 수정 가능(단단위 절사 등 실무 보정 — 2026-06-12).
  //   원화금액 수동 수정은 이 watch 를 다시 실행시키지 않으므로 보정값이 유지된다.
  watch(
    [() => formData.currency, () => formData.amount_foreign, () => formData.fx_rate],
    () => {
      if (formData.currency === "KRW") {
        formData.fx_rate = 1;
        formData.amount_foreign = formData.amount;
      } else {
        formData.amount = Math.round(formData.amount_foreign * formData.fx_rate * 100) / 100;
      }
    },
    { immediate: true }
  );

  // KRW 모드: 원화금액 입력 시 외화금액 동기화(데이터 정합).
  // 외화 모드에서는 원화금액 수동 보정을 덮어쓰지 않도록 아무 것도 하지 않음.
  watch(() => formData.amount, () => {
    if (formData.currency === "KRW") {
      formData.amount_foreign = formData.amount;
    }
  });

  // 결제방법 변경 → 통화 옵션 갱신
  // (기존 watchEffect(getter, cb) 형태는 watch API 오용이라 콜백이 실행되지 않았음 — 2026-06-11 수정)
  watch(() => formData.payment_method, () => updateCurrencyOptions());


  function goBack() {
    history.back();
  }


    //////////////////////////////////////////////////





</script>

<!--
  ⚠ 반드시 scoped 유지.
  과거 이 블록이 전역으로 새서 모바일 전 화면의 el-button 이 100% 폭으로 깨졌음 (2026-06-11 수정).
-->
<style scoped>
.withholding-hint { font-size: 12px; color: #e6a23c; margin: -8px 0 8px; }
.withholding-hint .wh-sub { color: #909399; margin-left: 6px; }
/* ===== 헤더 ===== */
.form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-bottom: 12px;
}
.form-header h2 {
  margin: 0;
  font-size: 20px;
}
.form-header-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* StatusDisplay 의 설명 문단은 헤더에서는 숨김 (칩처럼 한 줄만) */
.form-header-meta :deep(p) { display: none; }

.document-number {
  font-weight: bold;
  color: #42b983;
  background-color: #f5f7fa;
  padding: 3px 10px;
  border-radius: 5px;
  display: inline-block;
  font-variant-numeric: tabular-nums;
}

.expenditure-form {
  max-width: 100%;
  margin: 0 auto;
  padding: 12px;
}
.expenditure-form :deep(.el-card__body) {
  padding: 10px;
}
.expenditure-form :deep(.el-card) {
  margin-bottom: 12px;
}
.expenditure-form :deep(h5) {
  margin: 0 0 8px;
  font-size: 13px;
  color: #909399;
}

/* 섹션 헤더 (제목 + 우측 합계/버튼) */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.section-header h5 { margin: 0; }
.section-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mr-alloc-hint { font-size: 12px; color: #909399; line-height: 1.6; }
.evidence-hint { font-size: 12px; color: #b0611a; font-weight: 400; margin-left: 8px; }
.details-sum {
  font-size: 12px;
  font-weight: 600;
  color: #67c23a;
  font-variant-numeric: tabular-nums;
}
.details-sum--mismatch { color: #e6a23c; }
.empty-hint {
  padding: 10px 0 2px;
  font-size: 12px;
  color: #909399;
}

/* 검색 모달 트리거 입력란 — 클릭 가능함을 표시 */
.picker-input :deep(.el-input__inner) { cursor: pointer; }

/* ===== 액션 바 ===== */
.form-action-bar {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
}
.form-action-bar .el-button { margin-left: 0; }

@media (max-width: 768px) {
  .expenditure-form {
    padding: 8px;
    /* 하단 고정 액션바에 가리지 않도록 여유 확보 */
    padding-bottom: 64px;
  }

  /* 모바일: 저장/뒤로가기 하단 고정 */
  .form-action-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    background: #fff;
    border-top: 1px solid #ebeef5;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
    padding: 8px 12px calc(8px + env(safe-area-inset-bottom));
  }
  .form-action-bar .action-primary { flex: 1; }

  /* 모바일: 입고내역 테이블 가로 스크롤 허용 */
  .expenditure-form :deep(.el-table) {
    overflow-x: auto;
  }
}

.att-hint { font-size: 12px; color: #909399; font-weight: 400; }
.att-upload-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

/* 관리자 정정 다이얼로그 (2026-08-03) */
.corr-grid {
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 8px 10px;
  align-items: center;
  margin: 10px 0;
}
.corr-grid label { font-size: 13px; color: #606266; }
.mb-3 { margin-bottom: 12px; }
.mt-2 { margin-top: 8px; }
</style>
